import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Interfaces
import { INotificationSettings, IUserData } from '../../interfaces/user.interfaces';
import { ICountry, ILanguage } from '../../interfaces/core.interfaces';
import { updateProfileEmail } from './user.thunks';

export interface UserState {
  user: IUserData | null;
  settings: INotificationSettings;
  loading: boolean;
  error: boolean;
}

const initialState: UserState = {
  user: null,
  settings: {
    in_app_notification: false,
    email_notification: false,
    // sms_notification: false,
  },
  loading: false,
  error: false
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    profileRequest(state) {
      state.loading = true;
      state.error = false;
    },
    profileSuccess: (state, action: PayloadAction<IUserData>) => {
      state.user = action.payload;
      state.loading = false;
      state.error = false;
    },
    profileFailure: (state, { payload: error }: PayloadAction<any>) => {
      state.loading = false;
      state.error = true;
    },
    profileUpdateRequest(state) {
      state.loading = true;
      state.error = false;
    },
    profileUpdateSuccess: (state, action: PayloadAction<IUserData>) => {
      state.user = action.payload;
      state.loading = false;
      state.error = false;
    },
    profileUpdateFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    updateProfileEmail: (state, { payload: email }: PayloadAction<string>) => {
      if (!state.user) return;
      state.user = { ...state.user, email };
    },
    profileFromCounsellorUpdate: (state, { payload }: PayloadAction<{
      first_name: string,
      last_name: string,
      email: string,
      country: ICountry,
      languages: ILanguage[]
    }>) => {
      state.user = state?.user ? { ...state.user, ...payload } : null;
      state.loading = false;
      state.error = false;
    },
    counsellorStatusUpdate: (state, { payload: status }: PayloadAction<boolean>) => {
      state.user = state?.user ? { ...state.user, is_counsellor: status } : null;
      state.loading = false;
      state.error = false;
    },
    notificationSettingsUpdate: (state, { payload: settings }: PayloadAction<INotificationSettings>) => {
      state.settings = settings;
      state.loading = false;
      state.error = false;
    },
    changeStripeConnectedStatus(state, { payload: is_stripe_connected }: PayloadAction<boolean>) {
      state.user = state.user ? { ...state.user, is_stripe_connected } : null;
    },

    profileDeleteRequest(state) {
      state.loading = true;
      state.error = false;
    },
    profileDeleteSuccess: (state) => {
      state.loading = false;
      state.error = false;
    },
    profileDeleteFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    updatePhotoRequest(state) {
      state.loading = true;
      state.error = false;
    },
    updatePhotoSuccess: (state, action: PayloadAction<{ photo: string }>) => {
      state.user = state.user ? { ...state.user, photo: action.payload.photo } : null;
      state.loading = false;
      state.error = false;
    },
    updatePhotoFailure(state) {
      state.loading = false;
      state.error = true;
    },
    deletePhotoRequest(state) {
      state.loading = true;
      state.error = false;
    },
    deletePhotoSuccess: (state) => {
      state.user = state.user ? { ...state.user, photo: null } : null;
      state.loading = false;
      state.error = false;
    },
    deletePhotoFailure(state) {
      state.loading = false;
      state.error = true;
    },
    logoutRequest(state) {
      state.loading = true;
      state.error = false;
    },
    logoutSuccess: (state) => {
      state.user = null;
      state.loading = false;
      state.error = false;
    },
    logoutFailure: (state) => {
      state.loading = false;
      state.error = true;
    },
    deleteLogoutRequest: (state) => {
      state.user = null;
      state.loading = false;
      state.error = false;
    },
  }
});

export const UserSliceActions = userSlice.actions;
